import React, { useEffect, useRef, useState } from "react";
import { faEyeDropper } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PresetColorPicker, { PRESET_COLORS } from "./PresetColorPicker";
import {
  COLOR_PICKER_POPUP_MARGIN,
  COLOR_PICKER_POPUP_TOTAL_HEIGHT,
  COLOR_PICKER_POPUP_WIDTH,
  ColorPickerPopup,
  IColorPickerPopupPosition,
} from "./popup/ColorPickerPopup";
import "./presetAndCustomColorPicker.scss";

interface IPresetAndCustomColorPickerProps {
  colorValue: string;
  onChange: (updatedHexColor: string | null) => void;
  compact?: boolean;
  portalParentId?: string;
}

export default function PresetAndCustomColorPicker(props: IPresetAndCustomColorPickerProps) {
  const { colorValue, onChange, compact, portalParentId } = props;
  const customButtonRef = useRef<HTMLButtonElement>(null);
  const customPopupRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [customPopupState, setCustomPopupState] = useState<{
    open: boolean;
    position: IColorPickerPopupPosition;
  }>({
    open: false,
    position: null,
  });

  const presetColorSelected = PRESET_COLORS.some(presetColor => presetColor === colorValue);
  const customColorSelected = Boolean(colorValue) && !presetColorSelected;

  function closeCustomPopup() {
    setCustomPopupState({
      open: false,
      position: null,
    });
  }

  function calculatePopupPosition(buttonRect: DOMRect) {
    const computedX = buttonRect.x + buttonRect.width / 2;
    let y = buttonRect.y - COLOR_PICKER_POPUP_TOTAL_HEIGHT;
    if (y < 0) {
      y = buttonRect.y + buttonRect.height + COLOR_PICKER_POPUP_MARGIN;
    }

    const halfPopupWidth = COLOR_PICKER_POPUP_WIDTH / 2;
    const minX = halfPopupWidth + COLOR_PICKER_POPUP_MARGIN;
    const maxX = window.innerWidth - halfPopupWidth - COLOR_PICKER_POPUP_MARGIN;
    const x = Math.min(Math.max(computedX, minX), Math.max(minX, maxX));

    return { x, y };
  }

  function handleCustomPickerToggle() {
    const customButtonRect = customButtonRef.current?.getBoundingClientRect();
    if (customPopupState.open || !customButtonRect) {
      closeCustomPopup();
      return;
    }

    const { x, y } = calculatePopupPosition(customButtonRect);
    setCustomPopupState({
      open: true,
      position: {
        x,
        y,
      },
    });
  }

  function handleCustomColorChange(updatedHexColor: string | null) {
    if (!updatedHexColor) {
      return;
    }
    onChange(updatedHexColor);
  }

  function handlePresetPick(codeColor: string) {
    onChange(colorValue === codeColor ? null : codeColor);
    if (customPopupState.open) {
      closeCustomPopup();
    }
  }

  function handlePresetPalettePointerDown(event: React.PointerEvent<HTMLDivElement>) {
    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }
    const clickedCustomTrigger = customButtonRef.current?.contains(target) ?? false;
    if (!clickedCustomTrigger && customPopupState.open) {
      closeCustomPopup();
    }
  }

  useEffect(() => {
    function handleOutsideCustomPopupClick(event: MouseEvent | TouchEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const clickedInsideContainer = containerRef.current?.contains(target) ?? false;
      const clickedInsidePopup = customPopupRef.current?.contains(target) ?? false;
      const clickedCustomButton = customButtonRef.current?.contains(target) ?? false;
      if (!clickedInsideContainer && !clickedInsidePopup && !clickedCustomButton) {
        closeCustomPopup();
      }
    }

    document.addEventListener("mousedown", handleOutsideCustomPopupClick);
    document.addEventListener("touchstart", handleOutsideCustomPopupClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideCustomPopupClick);
      document.removeEventListener("touchstart", handleOutsideCustomPopupClick);
    };
  }, []);

  useEffect(() => {
    if (!customPopupState.open) {
      return;
    }

    function handleResizeWhileOpen() {
      const buttonRect = customButtonRef.current?.getBoundingClientRect();
      if (!buttonRect) {
        return;
      }
      const { x, y } = calculatePopupPosition(buttonRect);
      setCustomPopupState(prev => ({
        ...prev,
        position: { x, y },
      }));
    }

    window.addEventListener("resize", handleResizeWhileOpen);
    return () => window.removeEventListener("resize", handleResizeWhileOpen);
  }, [customPopupState.open, customPopupState.position]);

  return (
    <div ref={containerRef} className="color-picker-with-custom" onPointerDown={handlePresetPalettePointerDown}>
      <PresetColorPicker
        colorValue={colorValue ?? ""}
        onClick={handlePresetPick}
        compact={compact}
        customActionSelected={customColorSelected}
        customActionOnClick={handleCustomPickerToggle}
        customAction={
          <button
            ref={customButtonRef}
            type="button"
            className={(compact ? "colors-compact" : "colors-regular") + " color-picker-with-custom-trigger"}
          >
            <FontAwesomeIcon icon={faEyeDropper} />
          </button>
        }
      />
      {customPopupState.open && (
        <ColorPickerPopup
          ref={customPopupRef}
          position={customPopupState.position}
          hexColor={colorValue ?? ""}
          updateHexColor={handleCustomColorChange}
          closePopup={closeCustomPopup}
          portalParentId={portalParentId}
        />
      )}
    </div>
  );
}
